import { PlusOutlined } from '@ant-design/icons';
import { ChannelDoc } from '@gooduncles/gu-app-schema';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { Badge, Button, Space, Tabs, Tag, Tooltip } from 'antd';
import { FC, useCallback, useMemo, useState } from 'react';

import { formatDate } from 'src/utils/date-util';

import DownloadRawDataCell from 'src/atomic-components/atoms/AgCell/AgCellDownloadRawData';

import { ChannelTab } from './ChannelList';

import styles from './ChannelTable.module.scss';

interface ChannelTableProps {
  rowData: ChannelDoc[];
  activeTab: ChannelTab;
  generalCount: number;
  storeCount: number;
  onTabChange: (tab: ChannelTab) => void;
  onEdit: (channel: ChannelDoc) => void;
  onCreate: () => void;
}

const channelTypeConfig: Record<string, { label: string; color: string }> = {
  notice: { label: '공지', color: 'blue' },
  promotion: { label: '프로모션', color: 'purple' },
  store: { label: '매장', color: 'green' },
};

const channelModeConfig: Record<string, { label: string; color: string }> = {
  broadcast: { label: '단방향', color: 'cyan' },
  conversation: { label: '양방향', color: 'orange' },
};

const defaultColDef: ColDef<ChannelDoc> = {
  sortable: true,
  resizable: true,
  filter: true,
};

/** 유형 Tag 렌더러 */
const TypeCellRenderer: FC<ICellRendererParams<ChannelDoc>> = (params) => {
  if (!params.value) return null;
  const config = channelTypeConfig[params.value] ?? { label: params.value, color: 'default' };
  return <Tag color={config.color}>{config.label}</Tag>;
};

/** 모드 Tag 렌더러 */
const ModeCellRenderer: FC<ICellRendererParams<ChannelDoc>> = (params) => {
  if (!params.value) return null;
  const config = channelModeConfig[params.value] ?? { label: params.value, color: 'default' };
  return <Tag color={config.color}>{config.label}</Tag>;
};

/** 발송자 유형 Tag 렌더러 */
const SendersCellRenderer: FC<ICellRendererParams<ChannelDoc>> = (params) => {
  const values = params.value as string[] | undefined;
  if (!values?.length) return null;
  return (
    <Space size={4} wrap>
      {values.map((v) => (
        <Tag key={v} style={{ margin: 0 }}>
          {v}
        </Tag>
      ))}
    </Space>
  );
};

/** 액션 셀 렌더러 */
const createActionRenderer = (onEdit: (channel: ChannelDoc) => void) => {
  const ActionCellRenderer: FC<ICellRendererParams<ChannelDoc>> = (params) => {
    if (!params.data) return null;
    return (
      <Button type='link' size='small' onClick={() => onEdit(params.data!)}>
        수정
      </Button>
    );
  };
  return ActionCellRenderer;
};

const ChannelTable: FC<ChannelTableProps> = ({
  rowData,
  activeTab,
  generalCount,
  storeCount,
  onTabChange,
  onEdit,
  onCreate,
}) => {
  const [gridRef, setGridRef] = useState<AgGridReact<ChannelDoc> | null>(null);

  const ActionCellRenderer = useMemo(() => createActionRenderer(onEdit), [onEdit]);

  const columnDefs: ColDef<ChannelDoc>[] = useMemo(() => {
    const common: ColDef<ChannelDoc>[] = [
      {
        field: 'name',
        headerName: '채널명',
        minWidth: 160,
        flex: 1.5,
        cellStyle: { fontWeight: 500 },
      },
      {
        field: '_id',
        headerName: 'ID',
        minWidth: 180,
        flex: 1,
        cellStyle: { color: '#8c8c8c', fontSize: '12px' },
      },
    ];

    const generalColumns: ColDef<ChannelDoc>[] = [
      ...common,
      { field: 'type', headerName: '유형', width: 110, cellRenderer: TypeCellRenderer },
      { field: 'mode', headerName: '모드', width: 110, cellRenderer: ModeCellRenderer },
      { field: 'service', headerName: '서비스', width: 110 },
      {
        field: 'allowedSenderCollections',
        headerName: '발송자',
        minWidth: 160,
        flex: 1,
        cellRenderer: SendersCellRenderer,
      },
      { field: 'description', headerName: '설명', minWidth: 160, flex: 2 },
      {
        field: 'createdAt',
        headerName: '생성일',
        width: 150,
        valueFormatter: (params) => (params.value ? formatDate(new Date(params.value), 'yyyy-MM-dd HH:mm') : ''),
      },
      { headerName: '', width: 80, cellRenderer: ActionCellRenderer, sortable: false, filter: false },
    ];

    const storeColumns: ColDef<ChannelDoc>[] = [
      ...common,
      { field: 'mode', headerName: '모드', width: 110, cellRenderer: ModeCellRenderer },
      { field: 'service', headerName: '서비스', width: 110 },
      {
        field: 'allowedSenderCollections',
        headerName: '발송자',
        minWidth: 160,
        flex: 1,
        cellRenderer: SendersCellRenderer,
      },
      { field: 'description', headerName: '설명', minWidth: 200, flex: 3 },
      {
        field: 'createdAt',
        headerName: '생성일',
        width: 150,
        valueFormatter: (params) => (params.value ? formatDate(new Date(params.value), 'yyyy-MM-dd HH:mm') : ''),
      },
      { headerName: '', width: 80, cellRenderer: ActionCellRenderer, sortable: false, filter: false },
      { headerName: '액션', hide: true, cellRenderer: DownloadRawDataCell, sortable: false, filter: false },
    ];

    return activeTab === 'store' ? storeColumns : generalColumns;
  }, [activeTab, ActionCellRenderer]);

  const autoSizeAll = useCallback(() => {
    gridRef?.columnApi?.autoSizeAllColumns(true);
  }, [gridRef]);

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => onTabChange(key as ChannelTab)}
          size='small'
          items={[
            {
              key: 'general',
              label: (
                <span>
                  일반 채널{' '}
                  <Badge count={generalCount} showZero color='#8c8c8c' size='small' style={{ marginLeft: 4 }} />
                </span>
              ),
            },
            {
              key: 'store',
              label: (
                <span>
                  매장 채널 <Badge count={storeCount} showZero color='#8c8c8c' size='small' style={{ marginLeft: 4 }} />
                </span>
              ),
            },
          ]}
          tabBarStyle={{ marginBottom: 0 }}
        />
        <Tooltip title='새 채널 추가'>
          <Button type='primary' icon={<PlusOutlined />} onClick={onCreate}>
            채널 생성
          </Button>
        </Tooltip>
      </div>
      <div className={styles.gridWrapper}>
        <div className='ag-theme-alpine' style={{ width: '100%', height: '100%' }}>
          <AgGridReact
            ref={setGridRef}
            rowData={rowData}
            onGridReady={autoSizeAll}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            suppressScrollOnNewData={true}
            alwaysShowHorizontalScroll={true}
            rowHeight={42}
            headerHeight={40}
            animateRows={true}
            suppressCellFocus={true}
          />
        </div>
      </div>
    </div>
  );
};

export default ChannelTable;
